import {createSelector} from '@reduxjs/toolkit'

import {TimeUnit} from '@shared/helpers/date'

import {RootState} from './interfaces'

export const waitingSelector = (state: RootState) => state.waiting

export const ticketIdSelector = (state: RootState) => state.waiting.ticketId

export const waitingStatusSelector = (state: RootState) => state.waiting.status

export const waitingVersionSelector = (state: RootState) => state.waiting.version

export const waitingQueueSelector = (state: RootState) => state.waiting.queue

export const waitingSocketInfoSelector = createSelector(waitingSelector, ({socketAuthUrl, socketChannelNames}) => ({
    socketAuthUrl,
    socketChannelNames,
}))

/** 잔여 인원과 대기 시간 */
function getWaitInfo({
    bandwidth,
    waitNumber,
    lastEnterableWaitNumber,
}: {
    /** 초당 진입하는 사용자 */
    bandwidth: number
    /** 사용자의 대기 번호 */
    waitNumber: number
    /** 현재 진입 가능한 대기 번호 */
    lastEnterableWaitNumber: number
}) {
    /** 사용자 앞의 대기 인원 */
    const personnelNumber = Math.max(waitNumber - lastEnterableWaitNumber, 0)
    /** 남은 시간 */
    const time = Math.max((personnelNumber / bandwidth) * TimeUnit.SECONDS || 0, 0)
    return {
        personnelNumber,
        time,
    }
}

/** 전체 대기열의 잔여 인원과 대기 시간 */
export const globalWaitInfoSelector = createSelector(waitingSelector, ({globalWaitQueue, globalSubTicket}) => {
    if (!globalWaitQueue?.queueActive) {
        return {personnelNumber: 0, time: 0}
    }
    return getWaitInfo({
        /** 초당 진입하는 이용자 */
        bandwidth: globalWaitQueue?.bandwidth ?? 0,
        /** 사용자의 대기 번호 */
        waitNumber: globalSubTicket?.waitNumber ?? globalWaitQueue?.lastWaitNumber ?? 0,
        /** 현재 진입 가능한 대기 번호 */
        lastEnterableWaitNumber: globalWaitQueue?.lastEnterableWaitNumber ?? 0,
    })
})

/** 부분 대기열의 잔여 인원과 대기 시간 */
export const localWaitInfoSelector = createSelector(waitingSelector, ({subTicketAndWaitQueueList: subWaitQueues}) =>
    subWaitQueues.reduce(
        (prev, {waitQueue, subTicket, enterable}) => {
            // 발급된 티켓이 없거나 진입 가능 상태면 계산하지 않습니다
            if (!subTicket || !waitQueue?.queueActive || enterable) {
                return prev
            }

            const {personnelNumber, time} = getWaitInfo({
                /** 초당 진입하는 이용자 */
                bandwidth: waitQueue?.bandwidth ?? 0,
                /** 사용자의 대기 번호 */
                waitNumber: subTicket.waitNumber ?? 0,
                /** 현재 진입 가능한 대기 번호 */
                lastEnterableWaitNumber: waitQueue?.lastEnterableWaitNumber ?? 0,
            })
            return {
                personnelNumber: prev.personnelNumber + personnelNumber,
                time: prev.time + time,
            }
        },
        {personnelNumber: 0, time: 0},
    ),
)

export const waitingInfoSelector = createSelector(
    globalWaitInfoSelector,
    localWaitInfoSelector,
    (globalWaitQueueInfo, subWaitQueueInfo) => {
        const personnelNumber = globalWaitQueueInfo.personnelNumber + subWaitQueueInfo.personnelNumber
        const time = globalWaitQueueInfo.time + subWaitQueueInfo.time

        return {personnelNumber, time}
    },
)

/**
 * @description 대기열 V2 대기정보 (순서, 시간) 관리
 */
export const waitingQueueInfoSelector = createSelector(waitingQueueSelector, (queue) => {
    if (!queue?.queueActive) {
        return {personnelNumber: 0, time: 0}
    }
    return {
        personnelNumber: queue.position,
        time: queue.estimatedWaitSeconds * TimeUnit.SECONDS,
    }
})
